/**
 * @taj/domain — Logique métier pure (zéro I/O), partagée front ⇄ back.
 * Tout est en CENTIMES (entiers). Jamais de float monétaire.
 */

export interface PricedOption {
  priceDelta: number; // centimes
}
export interface PricedLine {
  unitPriceCents: number;
  qty: number;
  vatRate: number; // ex: 10 pour 10%
  options?: PricedOption[];
}

export interface OrderTotals {
  subtotalCents: number; // total TTC avant remise (les prix carte sont TTC en France)
  taxCents: number; // part de TVA incluse, agrégée par taux
  totalCents: number; // net à payer après remises
  vatByRate: Record<string, { baseHT: number; vat: number }>;
}

export interface DiscountInput {
  type: 'PERCENT' | 'AMOUNT';
  value: number; // % ou centimes
}

/** Prix TTC d'une ligne (article + options) × quantité. */
export function lineTotalCents(line: PricedLine): number {
  const optionsCents = (line.options ?? []).reduce((s, o) => s + o.priceDelta, 0);
  return (line.unitPriceCents + optionsCents) * line.qty;
}

/** Décompose un TTC en base HT + TVA pour un taux donné (centimes, arrondi). */
export function splitVat(ttcCents: number, vatRate: number): { baseHT: number; vat: number } {
  const baseHT = Math.round(ttcCents / (1 + vatRate / 100));
  return { baseHT, vat: ttcCents - baseHT };
}

/** Applique une remise sur un montant TTC (centimes). Ne descend jamais sous 0. */
export function applyDiscount(ttcCents: number, discount?: DiscountInput): number {
  if (!discount) return ttcCents;
  const cut =
    discount.type === 'PERCENT'
      ? Math.round((ttcCents * discount.value) / 100)
      : discount.value;
  return Math.max(0, ttcCents - cut);
}

/**
 * Totaux complets d'une commande, TVA ventilée par taux (multi-taux 5.5/10/20).
 * Les prix affichés sont TTC ; on en extrait la TVA. La remise est répartie
 * proportionnellement, puis la TVA est recalculée sur le net.
 */
export function computeOrderTotals(lines: PricedLine[], discount?: DiscountInput): OrderTotals {
  const subtotalCents = lines.reduce((s, l) => s + lineTotalCents(l), 0);
  const totalCents = applyDiscount(subtotalCents, discount);

  // Ratio de remise pour répartir sur chaque ligne (donc sur chaque taux de TVA).
  const ratio = subtotalCents > 0 ? totalCents / subtotalCents : 1;

  const vatByRate: Record<string, { baseHT: number; vat: number }> = {};
  let taxCents = 0;
  for (const l of lines) {
    const lineNet = Math.round(lineTotalCents(l) * ratio);
    const { baseHT, vat } = splitVat(lineNet, l.vatRate);
    const key = l.vatRate.toFixed(2);
    if (!vatByRate[key]) vatByRate[key] = { baseHT: 0, vat: 0 };
    vatByRate[key].baseHT += baseHT;
    vatByRate[key].vat += vat;
    taxCents += vat;
  }

  return { subtotalCents, taxCents, totalCents, vatByRate };
}

// ─────────── Fidélité ───────────
/** Points gagnés (1 pt / € TTC payé par défaut). */
export function loyaltyPointsEarned(totalCents: number, pointsPerEuro = 1): number {
  return Math.floor((totalCents / 100) * pointsPerEuro);
}

// ─────────── Livraison ───────────
export interface DeliveryZoneRule {
  feeCents: number;
  minOrderCents: number;
  freeAboveCents?: number | null;
}
export interface DeliveryQuote {
  eligible: boolean;
  feeCents: number;
  reason?: string;
}
export function deliveryQuote(orderCents: number, zone: DeliveryZoneRule): DeliveryQuote {
  if (orderCents < zone.minOrderCents) {
    return { eligible: false, feeCents: 0, reason: 'Minimum de commande non atteint' };
  }
  if (zone.freeAboveCents != null && orderCents >= zone.freeAboveCents) {
    return { eligible: true, feeCents: 0 };
  }
  return { eligible: true, feeCents: zone.feeCents };
}

// ─────────── Paiement mixte / reste à payer ───────────
export function remainingCents(totalCents: number, paidCents: number): number {
  return Math.max(0, totalCents - paidCents);
}
export function isFullyPaid(totalCents: number, paidCents: number): boolean {
  return paidCents >= totalCents;
}

// ─────────── Partenaires (Uber Eats / Deliveroo) ───────────
export function partnerSplit(grossCents: number, commissionPct: number) {
  const commissionCents = Math.round((grossCents * commissionPct) / 100);
  return { grossCents, commissionCents, netCents: grossCents - commissionCents };
}
